import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { money, formatApiError } from "../lib/api";

const empty = { code: "", type: "percent", value: 10, min_subtotal: 0, active: true, expires_at: "" };

export default function AdminCoupons() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/coupons").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editing,
        value: editing.type === "percent" ? parseFloat(editing.value) : Math.round(parseFloat(String(editing.value).replace(",", ".")) * 100),
        min_subtotal: Math.round(parseFloat(String(editing.min_subtotal || 0).replace(",", ".")) * 100),
        expires_at: editing.expires_at || null,
      };
      if (editing.id) await api.put(`/admin/coupons/${editing.id}`, payload);
      else await api.post("/admin/coupons", payload);
      toast.success("Cupom salvo");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Excluir cupom ${c.code}?`)) return;
    await api.delete(`/admin/coupons/${c.id}`);
    load();
  };

  const openEdit = (c) => setEditing(c ? {
    ...c,
    value: c.type === "percent" ? c.value : (c.value / 100).toFixed(2).replace(".", ","),
    min_subtotal: (c.min_subtotal / 100).toFixed(2).replace(".", ","),
    expires_at: c.expires_at ? c.expires_at.slice(0, 10) : "",
  } : { ...empty });

  return (
    <div data-testid="admin-coupons">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Cupons</h1>
          <p className="text-xs text-zinc-500 mt-2">Cupom e promoção não acumulam — vale o maior desconto.</p>
        </div>
        <Button onClick={() => openEdit(null)} className="bg-gold text-black hover:bg-gold-light" data-testid="new-coupon-button">
          <Plus size={15} className="mr-1.5" /> Novo cupom
        </Button>
      </div>

      <div className="border border-white/10 rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]" data-testid="coupons-table">
          <thead className="bg-white/5 text-zinc-400 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-5 py-3">Código</th>
              <th className="text-left px-5 py-3">Desconto</th>
              <th className="text-left px-5 py-3">Mínimo</th>
              <th className="text-left px-5 py-3">Expira</th>
              <th className="text-left px-5 py-3">Ativo</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-white/5" data-testid={`coupon-row-${c.code}`}>
                <td className="px-5 py-3.5 font-mono text-gold">{c.code}</td>
                <td className="px-5 py-3.5 text-zinc-200">{c.type === "percent" ? `${c.value}%` : money(c.value)}</td>
                <td className="px-5 py-3.5 text-zinc-400">{c.min_subtotal ? money(c.min_subtotal) : "—"}</td>
                <td className="px-5 py-3.5 text-zinc-400">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("pt-BR") : "—"}</td>
                <td className="px-5 py-3.5">
                  <Switch checked={!!c.active} onCheckedChange={async (v) => { await api.put(`/admin/coupons/${c.id}`, { active: v }); load(); }} data-testid="coupon-active-switch" />
                </td>
                <td className="px-5 py-3.5">
                  <div className="flex gap-2 justify-end">
                    <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-gold" onClick={() => openEdit(c)} data-testid="edit-coupon-button"><Pencil size={14} /></Button>
                    <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-red-400" onClick={() => remove(c)} data-testid="delete-coupon-button"><Trash2 size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-zinc-600">Nenhum cupom.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#111111] border-white/10" data-testid="coupon-dialog">
          <DialogHeader><DialogTitle className="font-display">{editing?.id ? "Editar cupom" : "Novo cupom"}</DialogTitle></DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4">
              <div>
                <Label>Código *</Label>
                <Input required value={editing.code} onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })} data-testid="coupon-code-input"
                  className="mt-1.5 bg-[#141414] border-white/10 font-mono" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Tipo</Label>
                  <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                    <SelectTrigger className="mt-1.5 bg-[#141414] border-white/10" data-testid="coupon-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#141414] border-white/10">
                      <SelectItem value="percent">Percentual (%)</SelectItem>
                      <SelectItem value="fixed">Valor fixo (R$)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Valor *</Label>
                  <Input required value={editing.value} onChange={(e) => setEditing({ ...editing, value: e.target.value })} data-testid="coupon-value-input"
                    className="mt-1.5 bg-[#141414] border-white/10" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Subtotal mínimo (R$)</Label>
                  <Input value={editing.min_subtotal} onChange={(e) => setEditing({ ...editing, min_subtotal: e.target.value })} data-testid="coupon-min-input"
                    className="mt-1.5 bg-[#141414] border-white/10" />
                </div>
                <div>
                  <Label>Expira em</Label>
                  <Input type="date" value={editing.expires_at} onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })} data-testid="coupon-expires-input"
                    className="mt-1.5 bg-[#141414] border-white/10" />
                </div>
              </div>
              <Button type="submit" className="w-full bg-gold text-black hover:bg-gold-light" data-testid="save-coupon-button">Salvar</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
